// ========================================
// DEPRECATED: This component is no longer in use
// The active ID verification functionality is in Tasks.tsx
// This file is kept for reference only - DO NOT MODIFY
// Last active route: /admin/photo-id (now using Tasks.tsx at /tasks)
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import { photoIdApi, PhotoIdTask, PhotoIdStats } from '../api/photoId';
import { useAuth } from '../context/AuthContext';
import './PhotoIdTasks.css';

// DEPRECATED - Use Tasks.tsx instead
const PhotoIdTasks: React.FC = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState<PhotoIdTask[]>([]);
  const [stats, setStats] = useState<PhotoIdStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<PhotoIdTask | null>(null);
  const [viewingAttachments, setViewingAttachments] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [processingTaskId, setProcessingTaskId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [sendingBatch, setSendingBatch] = useState(false);
  const [batchSize, setBatchSize] = useState(500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalTasks, setTotalTasks] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const fetchData = useCallback(async (options: { showLoading?: boolean } = {}) => {
    const showLoading = options.showLoading !== false;
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const offset = (currentPage - 1) * pageSize;
      
      // Fetch tasks and stats in parallel
      const [tasksData, statsData] = await Promise.all([
        photoIdApi.getVerificationTasksPaginated(statusFilter, pageSize, offset),
        photoIdApi.getStats()
      ]);
      
      setTasks(tasksData.tasks);
      setTotalTasks(typeof tasksData.count === 'number' ? tasksData.count : tasksData.tasks.length);
      setStats(statsData);
      
      // Debug logging
      console.log('Fetched tasks:', tasksData.tasks);
      if (tasksData.tasks.length > 0) {
        console.log('First task DOB:', tasksData.tasks[0].clientDOB);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
      console.error('Error fetching photo ID data:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [statusFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const calculatedTotalPages = Math.max(1, Math.ceil(totalTasks / pageSize));
    if (currentPage > calculatedTotalPages) {
      setCurrentPage(calculatedTotalPages);
    }
  }, [totalTasks, pageSize, currentPage]);

  const totalPages = Math.max(1, Math.ceil(totalTasks / pageSize));
  const pageStart = totalTasks === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = totalTasks === 0 ? 0 : Math.min(totalTasks, currentPage * pageSize);

  const handleApprove = async (task: PhotoIdTask) => {
    try {
      setProcessingTaskId(task.id);
      setError(null);
      
      const result = await photoIdApi.approveVerification(task.id);
      
      if (result.success) {
        // Remove task locally when viewing pending list
        if (statusFilter === 'pending') {
          setTasks(tasks.filter(t => t.id !== task.id));
        }
        
        // Update stats
        if (stats) {
          setStats({
            ...stats,
            clients: {
              ...stats.clients,
              pending: Math.max(0, stats.clients.pending - 1),
              verified: stats.clients.verified + 1
            },
            tasks: {
              pendingReview: Math.max(0, stats.tasks.pendingReview - 1)
            }
          });
        }

        await fetchData({ showLoading: false });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to approve verification');
    } finally {
      setProcessingTaskId(null);
    }
  };

  const handleDuplicate = async (task: PhotoIdTask) => {
    try {
      setProcessingTaskId(task.id);
      setError(null);
      
      const result = await photoIdApi.markAsDuplicate(task.id);
      
      if (result.success) {
        // Remove task locally when viewing pending list
        if (statusFilter === 'pending') {
          setTasks(tasks.filter(t => t.id !== task.id));
        }
        
        // Update stats
        if (stats) {
          setStats({
            ...stats,
            tasks: {
              pendingReview: Math.max(0, stats.tasks.pendingReview - 1)
            }
          });
        }
        
        // Close the modal if open
        setViewingAttachments(false);
        await fetchData({ showLoading: false });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark as duplicate');
    } finally {
      setProcessingTaskId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedTask || !rejectionReason.trim()) return;
    
    try {
      setProcessingTaskId(selectedTask.id);
      setError(null);
      
      const result = await photoIdApi.rejectVerification(selectedTask.id, rejectionReason);
      
      if (result.success) {
        // Remove task locally when viewing pending list
        if (statusFilter === 'pending') {
          setTasks(tasks.filter(t => t.id !== selectedTask.id));
        }
        
        // Update stats
        if (stats) {
          setStats({
            ...stats,
            clients: {
              ...stats.clients,
              pending: Math.max(0, stats.clients.pending - 1),
              rejected: stats.clients.rejected + 1
            },
            tasks: {
              pendingReview: Math.max(0, stats.tasks.pendingReview - 1)
            }
          });
        }
        
        setShowRejectionModal(false);
        setSelectedTask(null);
        setRejectionReason('');
        await fetchData({ showLoading: false });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reject verification');
    } finally {
      setProcessingTaskId(null);
    }
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === 'next' && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleViewAttachments = async (task: PhotoIdTask) => {
    try {
      // Reset error state
      setAttachmentError(null);
      
      // Set selected task and open modal
      setSelectedTask(task);
      setViewingAttachments(true);
      setLoadingAttachments(true);
      
      // Fetch attachments with SAS URLs
      const attachmentsData = await photoIdApi.getTaskAttachments(task.id);
      
      // Debug logging
      console.log('Raw attachments data:', attachmentsData);
      console.log('Task attachments before mapping:', attachmentsData.attachments);
      
      // Map secureUrl to url for each attachment
      const attachmentsWithUrl = attachmentsData.attachments.map(att => {
        // Check all possible URL fields
        const url = att.url || att.secureUrl || '';
        const mappedAttachment = {
          ...att,
          url: url,
          contentType: att.contentType || att.type || 'application/pdf',
          hasUrl: !!url  // Add flag to check if URL exists
        };
        console.log('Mapped attachment:', {
          filename: mappedAttachment.filename,
          url: mappedAttachment.url,
          hasUrl: mappedAttachment.hasUrl,
          originalSecureUrl: att.secureUrl,
          originalUrl: att.url
        });
        return mappedAttachment;
      });
      
      // Check if we have any URLs
      const hasAnyUrls = attachmentsWithUrl.some(att => att.hasUrl);
      if (!hasAnyUrls && attachmentsWithUrl.length > 0) {
        console.warn('No URLs found for any attachments');
        setAttachmentError('Unable to generate viewing URLs for attachments. Please try again later.');
      }
      
      // Update task with URLs
      setSelectedTask({
        ...task,
        attachments: attachmentsWithUrl
      });
      
      setLoadingAttachments(false);
      
      // Debug logging
      console.log('Final selected task with attachments:', {
        ...task,
        attachments: attachmentsWithUrl
      });
    } catch (err: any) {
      console.error('Error loading attachments:', err);
      setAttachmentError(err.message || 'Failed to load attachment URLs. Please try again.');
      setLoadingAttachments(false);
      // Keep modal open but show error
    }
  };

  const handleSendBatchHistoric = async () => {
    try {
      setSendingBatch(true);
      setError(null);
      
      const result = await photoIdApi.sendBatchHistoric(batchSize);
      
      if (result.sent > 0) {
        alert(`Successfully sent ${result.sent} ID requests`);
        await fetchData(); // Refresh stats
      } else if (result.failed > 0) {
        setError(`Failed to send ${result.failed} requests. Check console for details.`);
        console.error('Batch send errors:', result.errors);
      } else {
        alert('No eligible clients found for ID requests');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send batch requests');
    } finally {
      setSendingBatch(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading photo ID tasks...</p>
      </div>
    );
  }

  return (
    <div className="photo-id-tasks-page">
      <div className="page-header">
        <h1>Photo ID Verification</h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button className="close-error" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Clients</h3>
            <div className="stat-value">{stats.clients.total}</div>
          </div>
          <div className="stat-card stat-success">
            <h3>Verified</h3>
            <div className="stat-value">{stats.clients.verified}</div>
            <div className="stat-percentage">
              {stats.clients.total > 0 
                ? `${Math.round((stats.clients.verified / stats.clients.total) * 100)}%`
                : '0%'}
            </div>
          </div>
          <div className="stat-card stat-warning">
            <h3>Pending</h3>
            <div className="stat-value">{stats.clients.pending}</div>
            <div className="stat-subtitle">{stats.tasks.pendingReview} tasks to review</div>
          </div>
          <div className="stat-card stat-error">
            <h3>Rejected</h3>
            <div className="stat-value">{stats.clients.rejected}</div>
          </div>
          <div className="stat-card">
            <h3>Not Requested</h3>
            <div className="stat-value">{stats.clients.notRequested}</div>
            <button
              className="btn btn-primary btn-small"
              onClick={() => document.getElementById('batch-send-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Send Requests
            </button>
          </div>
        </div>
      )}

      <div className="tasks-section">
        <div className="section-header">
          <h2>Verification Tasks</h2>
          <div className="filter-tabs">
            <button 
              className={`tab ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending ({stats?.tasks.pendingReview || 0})
            </button>
            <button
              className={`tab ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setStatusFilter('approved')}
            >
              Approved
            </button>
            <button
              className={`tab ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter('rejected')}
            >
              Rejected
            </button>
          </div>
        </div>

        <div className="pagination-controls">
          <div className="page-info">
            {totalTasks === 0 ? 'No tasks to display' : `Showing ${pageStart}-${pageEnd} of ${totalTasks} tasks`}
          </div>
          <div className="pagination-actions">
            <label htmlFor="pageSizeSelect">
              Rows per page:
              <select id="pageSizeSelect" value={pageSize} onChange={handlePageSizeChange}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <div className="pagination-buttons">
              <button 
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1 || totalTasks === 0 || loading}
              >
                Previous
              </button>
              <span>
                Page {totalTasks === 0 ? 0 : currentPage} of {totalTasks === 0 ? 0 : totalPages}
              </span>
              <button 
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages || totalTasks === 0 || loading}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {tasks.length > 0 ? (
          <div className="tasks-grid">
            {tasks.map((task) => (
              <div key={task.id} className="task-card">
                <div className="task-header">
                  <h3>{task.clientName || 'Unknown Client'}</h3>
                  <span className={`task-status status-${task.status}`}>
                    {task.status}
                  </span>
                </div>
                
                <div className="task-info">
                  <div className="info-row">
                    <span className="label">Email:</span>
                    <span>{task.clientEmail}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Claims:</span>
                    <span>{task.associatedClaimCount || 0}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Submitted:</span>
                    <span>{new Date(task.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Attachments:</span>
                    <span>{task.attachments.length} file(s)</span>
                  </div>
                </div>

                {task.aiProcessed && task.aiNotes && (
                  <div className="ai-notes-section">
                    <div className="ai-notes-header">
                      <span className="ai-icon">🤖</span>
                      <span className="ai-label">AI Analysis:</span>
                      {task.aiShouldRetry && task.aiNextRetryAt && (
                        <span className="ai-retry-info">
                          Retry {task.aiRetryCount || 1}/3 scheduled for {new Date(task.aiNextRetryAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="ai-notes-content">
                      {task.aiNotes.split('\n').map((line, index) => (
                        <div key={index} className="ai-note-line">{line}</div>
                      ))}
                    </div>
                  </div>
                )}

                {task.status === 'pending' && (
                  <div className="task-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleViewAttachments(task)}
                    >
                      View ID
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleApprove(task)}
                      disabled={processingTaskId === task.id}
                    >
                      {processingTaskId === task.id ? 'Processing...' : 'Approve'}
                    </button>
                    <button
                      className="btn btn-error"
                      onClick={() => {
                        setSelectedTask(task);
                        setShowRejectionModal(true);
                      }}
                      disabled={processingTaskId === task.id}
                    >
                      Reject
                    </button>
                  </div>
                )}

                {task.status === 'rejected' && task.rejectionReason && (
                  <div className="rejection-reason">
                    <strong>Reason:</strong> {task.rejectionReason}
                  </div>
                )}

                {task.status !== 'pending' && task.processedBy && (
                  <div className="processed-info">
                    Processed by {task.processedBy} on{' '}
                    {task.processedAt && new Date(task.processedAt).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-tasks">
            <p>No {statusFilter} tasks found</p>
          </div>
        )}
      </div>

      <div id="batch-send-section" className="batch-send-section">
        <h2>Send Batch ID Requests</h2>
        <p>Send ID verification requests to historic clients who haven't been asked yet.</p>
        
        <div className="batch-controls">
          <div className="input-group">
            <label>Batch Size:</label>
            <input
              type="number"
              min="1"
              max="1000"
              value={batchSize}
              onChange={(e) => setBatchSize(parseInt(e.target.value) || 500)}
              className="form-input"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleSendBatchHistoric}
            disabled={sendingBatch}
          >
            {sendingBatch ? 'Sending...' : `Send to ${batchSize} Clients`}
          </button>
        </div>
        
        {stats && (
          <p className="batch-info">
            {stats.clients.notRequested} clients have not received ID requests yet.
          </p>
        )}
      </div>

      {/* Attachments Modal */}
      {viewingAttachments && selectedTask && (
        <div className="modal-overlay" onClick={() => {
          setViewingAttachments(false);
        }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review ID Verification</h2>
              <button className="modal-close" onClick={() => {
                setViewingAttachments(false);
              }}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="task-summary">
                <div className="summary-row">
                  <span className="summary-label">Task Type:</span>
                  <span>ID Verification</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Client:</span>
                  <span>{selectedTask.clientName || 'Unknown'}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Email:</span>
                  <span>{selectedTask.clientEmail}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Date of Birth:</span>
                  <span>{selectedTask.clientDOB || 'Not provided'}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Claims:</span>
                  <span>{selectedTask.associatedClaimCount || 0}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Submitted:</span>
                  <span>{new Date(selectedTask.createdAt).toLocaleString()}</span>
                </div>
              </div>
              
              {selectedTask.aiProcessed && (
                <div className="ai-analysis-results">
                  <h3>AI Analysis Results</h3>
                  <div className="ai-results-content">
                    <div className="ai-result-row">
                      <span className="ai-result-label">AI Confidence Score:</span>
                      <span className={`ai-confidence-score ${selectedTask.aiConfidence && selectedTask.aiConfidence >= 90 ? 'high' : 'low'}`}>
                        {selectedTask.aiConfidence ? `${selectedTask.aiConfidence.toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="ai-result-row">
                      <span className="ai-result-label">Analyzed:</span>
                      <span>{selectedTask.processedAt ? new Date(selectedTask.processedAt).toLocaleString() : 'Not yet'}</span>
                    </div>
                    {selectedTask.aiNotes && (
                      <div className="ai-notes-box">
                        <div className="ai-notes-label">AI Notes:</div>
                        <div className="ai-notes-text">
                          {selectedTask.aiNotes.split('\n').map((line, index) => (
                            <div key={index} className="ai-note-line">{line}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <h3 className="documents-heading">Uploaded Documents</h3>
              
              {attachmentError && (
                <div className="attachment-error">
                  <span className="error-icon">⚠️</span>
                  <p>{attachmentError}</p>
                  <button 
                    className="btn btn-small"
                    onClick={() => handleViewAttachments(selectedTask)}
                  >
                    Retry
                  </button>
                </div>
              )}
              
              <div className="attachments-list">
                {selectedTask.attachments && selectedTask.attachments.length > 0 ? (
                  selectedTask.attachments.map((attachment, index) => {
                    // Determine file type from contentType or type field
                    const fileType = attachment.contentType || attachment.type || '';
                    const isImage = fileType.startsWith('image/');
                    const isPdf = fileType.includes('pdf') || attachment.filename?.toLowerCase().endsWith('.pdf');
                    const fileSize = attachment.size || attachment.sizeBytes || 0;
                    const attachmentUrl = attachment.url || attachment.secureUrl || '';
                    
                    return (
                      <div key={index} className="attachment-row">
                        <div className="attachment-icon">
                          {isPdf ? '📄' : isImage ? '🖼️' : '📎'}
                        </div>
                        <div className="attachment-details">
                          <div className="attachment-name">{attachment.filename}</div>
                          <div className="attachment-size">
                            {fileSize ? `${(fileSize / 1024).toFixed(2)} KB` : 'Unknown size'}
                          </div>
                          <div className="attachment-type">{fileType || 'application/pdf'}</div>
                        </div>
                        <div className="attachment-actions">
                          {loadingAttachments ? (
                            <button className="btn btn-secondary btn-small" disabled>
                              Loading...
                            </button>
                          ) : attachmentUrl ? (
                            <a
                              href={attachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary btn-small"
                              download={attachment.filename}
                              onClick={(e) => {
                                console.log('Opening attachment:', {
                                  filename: attachment.filename,
                                  url: attachmentUrl
                                });
                              }}
                            >
                              {isPdf ? 'Download PDF' : isImage ? 'View Image' : 'Download'}
                            </a>
                          ) : (
                            <button 
                              className="btn btn-secondary btn-small"
                              onClick={() => handleViewAttachments(selectedTask)}
                            >
                              Load URL
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="no-attachments">No attachments found</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {/* Debug info - remove after testing */}
              {(() => {
                console.log('Debug - isAdmin:', isAdmin, 'aiProcessed:', selectedTask.aiProcessed, 'task:', selectedTask);
                return null;
              })()}
              {isAdmin && selectedTask.aiProcessed && (
                <button
                  className="btn btn-secondary"
                  style={{ marginRight: 'auto' }}
                  onClick={async () => {
                    try {
                      setProcessingTaskId(selectedTask.id);
                      const result = await photoIdApi.resetAiProcessing(selectedTask.id);
                      if (result.success) {
                        alert('AI processing reset successfully. Task will be reprocessed on next run.');
                        setViewingAttachments(false);
                        await fetchData();
                      } else {
                        setError(result.message || 'Failed to reset AI processing');
                      }
                    } catch (err: any) {
                      setError(err.message || 'Failed to reset AI processing');
                    } finally {
                      setProcessingTaskId(null);
                    }
                  }}
                  disabled={processingTaskId === selectedTask.id}
                >
                  🔄 Reset AI Processing
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setViewingAttachments(false);
                }}
              >
                Close
              </button>
              {selectedTask.status === 'pending' && (
                <>
                  <button
                    className="btn btn-warning"
                    onClick={() => {
                      handleDuplicate(selectedTask);
                    }}
                    disabled={processingTaskId === selectedTask.id}
                  >
                    {processingTaskId === selectedTask.id ? 'Processing...' : 'Duplicate'}
                  </button>
                  <button
                    className="btn btn-error"
                    onClick={() => {
                      setViewingAttachments(false);
                      setShowRejectionModal(true);
                    }}
                    disabled={processingTaskId === selectedTask.id}
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      handleApprove(selectedTask);
                      setViewingAttachments(false);
                    }}
                    disabled={processingTaskId === selectedTask.id}
                  >
                    {processingTaskId === selectedTask.id ? 'Processing...' : 'Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject ID Verification</h2>
              <button className="modal-close" onClick={() => setShowRejectionModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for rejecting this ID verification:</p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., ID image is not clear, ID has expired, etc."
                className="form-textarea"
                rows={4}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowRejectionModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={handleReject}
                disabled={!rejectionReason.trim() || processingTaskId === selectedTask.id}
              >
                {processingTaskId === selectedTask.id ? 'Processing...' : 'Reject & Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoIdTasks;
